import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { deleteLeistungen } from '../lib/api.js';
import { sendOrQueue, remove as removeQueued } from '../lib/outbox.js';
import { load, save } from '../lib/storage.js';
import { hours, isoDate, roundQuarter } from '../lib/format.js';
import { useToast } from './toast.jsx';
import { stundenNachfuehren } from '../hooks/data.js';

/**
 * Laufende Zeit – lokal gespeichert, überlebt App-Neustart und Funklöcher.
 * Gebucht wird erst beim Stoppen – immer auf den angemeldeten Exxas-Benutzer.
 *
 * Zustand: { running, startedAt, nummer, titel, beschreibung, leistungsartId }
 */
const TimerCtx = createContext(null);
const KEY = 'timer';

export function TimerProvider({ children }) {
  const [t, setT] = useState(() => load(KEY, { running: false }));
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => { save(KEY, t); }, [t]);
  useEffect(() => {
    if (!t.running) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [t.running]);

  const elapsedSec = t.running ? Math.max(0, (now - t.startedAt) / 1000) : 0;

  /** Laufende Zeit auf den Timer-Auftrag buchen – ohne Netz landet sie in der Outbox. */
  const book = useCallback(async (cur) => {
    const stunden = roundQuarter(Date.now() - cur.startedAt);
    const r = await sendOrQueue({
      kind: 'leistung',
      nummer: cur.nummer,
      uid: qc.getQueryData(['me'])?.uid ?? null,
      label: `${hours(stunden)} ${cur.beschreibung || 'Arbeitszeit'}`,
      path: `/auftraege/${encodeURIComponent(cur.nummer)}/leistungen`,
      body: {
        datum: isoDate(new Date(cur.startedAt)),
        stunden,
        beschreibung: cur.beschreibung || '',
        leistungsartId: cur.leistungsartId,
      },
    });
    qc.invalidateQueries({ queryKey: ['leistungen', cur.nummer] });
    if (!r.queued) stundenNachfuehren(qc, { datum: isoDate(new Date(cur.startedAt)), stunden, nummer: cur.nummer, titel: cur.titel });
    return r.queued ? { stunden, erstellt: [], queuedId: r.id } : { stunden, erstellt: r.res.erstellt };
  }, [qc]);

  const undoFor = useCallback((nummer, erstellt) => async () => {
    const fehler = await deleteLeistungen(nummer, erstellt.map((e) => e.id));
    qc.invalidateQueries({ queryKey: ['leistungen', nummer] });
    toast(fehler.length
      ? { text: `Nicht zurückgenommen: ${fehler[0]}`, error: true }
      : { text: 'Buchung zurückgenommen' });
  }, [qc, toast]);

  const start = useCallback(({ nummer, titel, beschreibung, leistungsartId }) => {
    setT({ running: true, startedAt: Date.now(), nummer, titel, beschreibung, leistungsartId });
    setNow(Date.now());
    toast({ text: `Zeit läuft auf ${nummer}` });
  }, [toast]);

  const stop = useCallback(async () => {
    if (!t.running || busy) return;
    setBusy(true);
    try {
      const { stunden, erstellt, queuedId } = await book(t);
      setT({ running: false, last: { leistungsartId: t.leistungsartId, beschreibung: t.beschreibung } });
      toast(queuedId
        ? { text: `Vorgemerkt: ${hours(stunden)} auf ${t.nummer} werden automatisch gebucht, sobald Netz da ist`, long: true, undo: () => { removeQueued(queuedId); toast({ text: 'Buchung zurückgenommen' }); } }
        : { text: `${hours(stunden)} auf ${t.nummer} gebucht`, undo: undoFor(t.nummer, erstellt) });
    } catch (err) {
      toast({ text: `Nicht gebucht: ${err.message.replace(/\.$/, '')}. Zeit läuft weiter.`, error: true });
    } finally {
      setBusy(false);
    }
  }, [t, busy, book, toast, undoFor]);

  /** Laufende Zeit buchen und sofort auf einem anderen Auftrag weiterlaufen lassen. */
  const switchTo = useCallback(async ({ nummer, titel }) => {
    if (!t.running || t.nummer === nummer || busy) return;
    setBusy(true);
    try {
      const { stunden, queuedId } = await book(t);
      setT({ ...t, startedAt: Date.now(), nummer, titel });
      toast({ text: `${hours(stunden)} auf ${t.nummer} ${queuedId ? 'vorgemerkt' : 'gebucht'} · läuft jetzt auf ${nummer}` });
    } catch (err) {
      toast({ text: `Wechsel fehlgeschlagen: ${err.message}`, error: true });
    } finally {
      setBusy(false);
    }
  }, [t, busy, book, toast]);

  /** Einstellungen der laufenden Zeit ändern (Tätigkeit, Leistungsart). */
  const update = useCallback((patch) => setT((cur) => ({ ...cur, ...patch })), []);

  const value = useMemo(() => ({ ...t, elapsedSec, busy, start, stop, switchTo, update }), [t, elapsedSec, busy, start, stop, switchTo, update]);
  return <TimerCtx.Provider value={value}>{children}</TimerCtx.Provider>;
}

export const useTimer = () => useContext(TimerCtx);
